// src/app/modules/demandes/UsersPickerModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../../RessourcesStyles/UsersListModal.css";
import "../../RessourcesStyles/CollaboratorSelection.css";

const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

const norm = (v) => String(v ?? "").trim().toLowerCase();

function buildLabel(u) {
    const first = u?.firstName || u?.prenom || "";
    const last = u?.lastName || u?.nom || "";
    return `${first} ${last}`.trim() || u?.username || u?.login || `User#${u?.id ?? ""}`;
}

/* ── SVG Icons (inline, no external lib) ── */
const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const UsersIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);
const EmptyIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <line x1="18" y1="8" x2="23" y2="13" /><line x1="23" y1="8" x2="18" y2="13" />
    </svg>
);

export default function UsersPickerModal({
                                             open,
                                             onClose,
                                             users = [],
                                             title = "Sélectionner des collaborateurs",
                                             onConfirm, // (selectedIds:number[]) => void
                                             assignedIds = [], // IDs déjà affectés / inscrits
                                         }) {
    const [search, setSearch] = useState("");
    const [selectedPoste, setSelectedPoste] = useState("");
    const [selectedEntite, setSelectedEntite] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [selected, setSelected] = useState(() => new Set());
    
    const alreadyAssignedSet = useMemo(() => new Set(assignedIds || []), [assignedIds]);

    useEffect(() => {
        if (!open) return;
        setSearch("");
        setSelectedPoste("");
        setSelectedEntite("");
        setPage(1);
        setPageSize(DEFAULT_PAGE_SIZE);
        setSelected(new Set());
    }, [open]);

    const availablePostes = useMemo(() => {
        const set = new Set();
        users.forEach(u => {
            const p = u?.jobName || u?.jobTitle || u?.poste || u?.jobText;
            if (p) set.add(p);
        });
        return Array.from(set).sort();
    }, [users]);

    const availableEntites = useMemo(() => {
        const set = new Set();
        users.forEach(u => {
            const e = u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName || u?.departement;
            if (e) set.add(e);
        });
        return Array.from(set).sort();
    }, [users]);

    const filtered = useMemo(() => {
        const list = Array.isArray(users) ? users : [];
        const q = norm(search);

        return list.filter((u) => {
            const name = norm(buildLabel(u));
            const matricule = norm(u?.matricule || u?.employeeCode);
            const email = norm(u?.email || u?.mail);
            const entityVal = u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName || u?.departement;
            const posteVal = u?.jobName || u?.jobTitle || u?.poste || u?.jobText;
            
            const matchesSearch = !q || name.includes(q) || matricule.includes(q) || email.includes(q) || norm(entityVal).includes(q) || norm(posteVal).includes(q);
            
            const userPoste = u?.jobName || u?.jobTitle || u?.poste || u?.jobText || "";
            const userEntite = u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName || u?.departement || "";
            
            const matchesPoste = !selectedPoste || userPoste === selectedPoste;
            const matchesEntite = !selectedEntite || userEntite === selectedEntite;

            return matchesSearch && matchesPoste && matchesEntite;
        });
    }, [users, search, selectedPoste, selectedEntite]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const start = (pageSafe - 1) * pageSize;
    const end = Math.min(start + pageSize, filtered.length);
    const pageItems = filtered.slice(start, end);

    const selectedCount = selected.size;
    const pageSelectableIds = useMemo(() => 
        pageItems
            .filter((u) => u?.id != null && !alreadyAssignedSet.has(u.id))
            .map((u) => u.id),
    [pageItems, alreadyAssignedSet]);
    
    const pageIds = useMemo(() => pageItems.map((u) => u?.id).filter((id) => id != null), [pageItems]);
    const allPageSelected = pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selected.has(id));
    const somePageSelected = pageIds.some((id) => selected.has(id));

    const toggle = (id) => {
        if (id == null) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllPage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allPageSelected) {
                pageSelectableIds.forEach((id) => next.delete(id));
            } else {
                pageSelectableIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const confirm = () => {
        const ids = Array.from(selected.values());
        onConfirm?.(ids);
    };

    const hasActiveFilters = !!(search || selectedPoste || selectedEntite);
    const showPagination = filtered.length > pageSize;

    if (!open) return null;

    return (
        <div className="collaborator-selection-overlay" onMouseDown={onClose}>
            <div className="collaborator-selection-card" onMouseDown={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="collaborator-selection-header">
                    <div className="collaborator-selection-header-info">
                        <div className="collaborator-selection-title">{title}</div>
                        <div className="collaborator-selection-subtitle">
                            {filtered.length} collaborateur(s) disponible(s)
                            {selectedCount > 0 && ` · ${selectedCount} sélectionné(s)`}
                        </div>
                    </div>
                    <button className="collaborator-selection-close" type="button" onClick={onClose} aria-label="Fermer">
                        ✕
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="collaborator-selection-body">

                    {/* Search + Filters */}
                    <div className="collaborator-selection-filters">
                        <div className="collaborator-selection-search" style={{ gridColumn: "1 / span 2" }}>
                            <label>Recherche</label>
                            <div className="collaborator-selection-search-input">
                                <span className="collaborator-selection-search-icon"><SearchIcon /></span>
                                <input
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    placeholder="Rechercher par nom, matricule, poste, email..."
                                />
                            </div>
                        </div>

                        <div className="collaborator-selection-filter-field">
                            <label>Poste</label>
                            <select
                                value={selectedPoste}
                                onChange={(e) => { setSelectedPoste(e.target.value); setPage(1); }}
                            >
                                <option value="">Tous les postes</option>
                                {availablePostes.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="collaborator-selection-filters" style={{ gridTemplateColumns: "1fr 1fr auto", marginBottom: 14 }}>
                        <div className="collaborator-selection-filter-field">
                            <label>Entité</label>
                            <select
                                value={selectedEntite}
                                onChange={(e) => { setSelectedEntite(e.target.value); setPage(1); }}
                            >
                                <option value="">Toutes les entités</option>
                                {availableEntites.map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                        </div>
                        <div />
                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                            <button
                                className="cs-btn cs-btn--ghost"
                                type="button"
                                onClick={() => { setSearch(""); setSelectedPoste(""); setSelectedEntite(""); setPage(1); }}
                                disabled={!hasActiveFilters}
                            >
                                Effacer les filtres
                            </button>
                        </div>
                    </div>

                    {/* Actions row */}
                    <div className="collaborator-selection-actions-row">
                        <div className="collaborator-selection-actions-left">
                            <button
                                className="cs-btn cs-btn--ghost"
                                type="button"
                                onClick={toggleAllPage}
                                disabled={pageSelectableIds.length === 0}
                            >
                                {allPageSelected ? "Désélectionner la page" : "Tout sélectionner (page)"}
                            </button>
                            {selectedCount > 0 && (
                                <button className="cs-btn cs-btn--ghost" type="button" onClick={clearSelection}>
                                    Désélectionner tout
                                </button>
                            )}
                        </div>
                        <div className="collaborator-selection-actions-right">
                            {selectedCount > 0 && (
                                <div className="collaborator-selection-counter">
                                    <span className="collaborator-selection-counter-dot" />
                                    {selectedCount} collaborateur(s) sélectionné(s)
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="collaborator-selection-table-wrap collaborator-selection-table-wrap--tall">
                        <table className="collaborator-selection-table">
                            <thead>
                            <tr>
                                <th style={{ width: 44 }}>
                                    <input
                                        type="checkbox"
                                        checked={allPageSelected}
                                        ref={(el) => { if (el) el.indeterminate = !allPageSelected && somePageSelected; }}
                                        onChange={toggleAllPage}
                                        aria-label="Sélectionner la page"
                                    />
                                </th>
                                <th>Collaborateur</th>
                                <th>Matricule</th>
                                <th>Email</th>
                                <th>Poste</th>
                                <th>Entité</th>
                                <th className="cs-center">Statut</th>
                            </tr>
                            </thead>

                            <tbody>
                            {pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="collaborator-selection-empty">
                                            <div className="collaborator-selection-empty-icon"><EmptyIcon /></div>
                                            <div className="collaborator-selection-empty-text">
                                                {hasActiveFilters
                                                    ? "Aucun collaborateur ne correspond à votre recherche"
                                                    : "Aucun collaborateur disponible"}
                                            </div>
                                            {hasActiveFilters && (
                                                <div className="collaborator-selection-empty-hint">
                                                    Essayez de modifier vos critères de recherche
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((u) => {
                                    const id = u?.id;
                                    const alreadyAssigned = id != null && alreadyAssignedSet.has(id);
                                    const checked = selected.has(id);

                                    return (
                                        <tr 
                                            key={id ?? buildLabel(u)}
                                            className={`${alreadyAssigned ? "collaborator-selection-row--disabled" : ""} ${checked ? "collaborator-selection-row--selected" : ""}`}
                                            onClick={() => !alreadyAssigned && toggle(id)}
                                            style={{ cursor: alreadyAssigned ? "not-allowed" : "pointer" }}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={alreadyAssigned}
                                                    onChange={(e) => e.stopPropagation()}
                                                    aria-label={`Sélectionner ${buildLabel(u)}`}
                                                    readOnly
                                                />
                                            </td>
                                            <td>
                                                <div className="collaborator-selection-user-name">{buildLabel(u)}</div>
                                            </td>
                                            <td>{u?.matricule || u?.employeeCode || "—"}</td>
                                            <td>{u?.email || u?.mail || "—"}</td>
                                            <td>{u?.jobName || u?.jobTitle || u?.poste || u?.jobText || "—"}</td>
                                            <td>{u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName || u?.departement || "—"}</td>
                                            <td className="cs-center">
                                                <span className={`collaborator-selection-status ${alreadyAssigned ? "collaborator-selection-status--assigned" : "collaborator-selection-status--available"}`}>
                                                    {alreadyAssigned ? "Déjà inscrit" : "Disponible"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {showPagination && (
                        <div className="collaborator-selection-pagination">
                            <div className="collaborator-selection-pagination-nav">
                                <button className="cs-btn cs-btn--nav" type="button" onClick={() => setPage(1)} disabled={pageSafe <= 1}>
                                    ««
                                </button>
                                <button className="cs-btn cs-btn--nav" type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1}>
                                    Précédent
                                </button>
                            </div>

                            <div className="collaborator-selection-pagination-info">
                                <strong>{start + 1}</strong> – <strong>{end}</strong> sur <strong>{filtered.length}</strong> collaborateur(s)
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div className="collaborator-selection-pagination-size">
                                    <span>Afficher</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    >
                                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="collaborator-selection-pagination-nav">
                                    <button className="cs-btn cs-btn--nav" type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}>
                                        Suivant
                                    </button>
                                    <button className="cs-btn cs-btn--nav" type="button" onClick={() => setPage(totalPages)} disabled={pageSafe >= totalPages}>
                                        »»
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="collaborator-selection-footer">
                    <div className="collaborator-selection-footer-left">
                        {selectedCount > 0 && (
                            <div className="collaborator-selection-counter">
                                <UsersIcon />
                                {selectedCount} sélectionné(s)
                            </div>
                        )}
                    </div>
                    <div className="collaborator-selection-footer-right">
                        <button className="cs-btn cs-btn--secondary" type="button" onClick={onClose}>
                            Annuler
                        </button>
                        <button className="cs-btn cs-btn--primary" type="button" onClick={confirm} disabled={selectedCount === 0}>
                            Confirmer la sélection ({selectedCount})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
